import { useEffect, useState } from 'react';
import '../App.scss';
import { Button } from '@mui/base/Button';
import { TextField, FormControlLabel, Switch } from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import { UserClient, UserModel } from '../types';
import { createClient } from "../api/clients";

function EditUser() {
  const navigate = useNavigate();
  const location = useLocation();

  const returnTo =
    (location.state as { returnTo?: string })?.returnTo ??
    '/team-management';

  const { id } = useParams<{ id: string }>();
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [email, setEmail] = useState('');
  const [isEnabled, setIsEnabled] = useState(true);
  const [isLead, setIsLead] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const userClient = createClient(UserClient);

  useEffect(() => {
    const loadUser = async () => {
      if (!id) {
        setErrorMessage('No user id provided.');
        setLoading(false);
        return;
      }

      setErrorMessage(null);
      setLoading(true);

      try {
        const result: UserModel = await userClient.getUserById(id);

        setName(result.name ?? "");
        setRole(result.role ?? "");
        setEmail(result.email ?? "");
        setIsEnabled(result.isEnabled ?? true);
        setIsLead(result.isLead ?? false);

        setLoading(false);
      } catch (error) {
        console.error('Error loading user:', error);
        setErrorMessage('Unable to load user details. Please try again.');
        setLoading(false);
      }
    };

    loadUser();
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!id) {
      setErrorMessage('No user id provided.');
      return;
    }

    const user: UserModel = { id, name, role, isEnabled, isLead, email };

    try {
      const result = await userClient.editUser(user);

      if (result.success === false) {
        setErrorMessage(result.error ?? null);
        return;
      }

      navigate(returnTo, {
        replace: true,
        state: { message: 'User updated successfully' }
      });
    } catch (error) {
      console.error('Error updating user:', error);
      setErrorMessage('Something went wrong. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="main pb-4 d-flex flex-column align-items-center">
        <h4 className="mb-6">Edit team member</h4>
        <p>Loading user details…</p>
      </div>
    );
  }

  return (
    <div className="main pb-4 d-flex flex-column align-items-center">
      <Button
        className="styled-button mb-4"
        onClick={() => navigate(returnTo)}
      >
        ← Back
      </Button>

      <h4 className="mb-6">Edit team member</h4>

      <form onSubmit={handleSubmit} className="d-flex flex-column align-items-center">
        <TextField
          id="name"
          label="Name"
          variant="outlined"
          className="mb-4"
          style={{ minWidth: '300px' }}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        

        <TextField
          id="role"
          label="Role"
          variant="outlined"
          className="mb-4"
          style={{ minWidth: '300px' }}
          value={role}
          onChange={(e) => setRole(e.target.value)}
          required
        />
        
         <TextField
          id="email"
          label="Email"
          variant="outlined"
          className="mb-4"
          style={{ minWidth: '300px' }}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />

        <FormControlLabel
          control={
            <Switch
              checked={isEnabled}
              onChange={(e) => setIsEnabled(e.target.checked)}
              color="primary"
            />
          }
          label="Enabled"
          className="mb-4"
        />

        <FormControlLabel
          control={
            <Switch
              checked={isLead}
              onChange={(e) => setIsLead(e.target.checked)}
              color="primary"
            />
          }
          label="Lead User"
          className="mb-4"
        />

        {errorMessage && (
          <div className="form-error mb-3">
            {errorMessage}
          </div>
        )}

        <Button type="submit" className="styled-button mb-4" id="btn-edit-user">
          Update user
        </Button>
      </form>
    </div>
  );
}

export default EditUser;
